/**
 * "Ask your AI to edit", the two ways side by side: Black Cherry (fast) and the connector (best):
 * - Your AI app: the CherryClipz connector in ChatGPT / Claude / Gemini, where you watch it edit
 *   in the chat. Set up on the Connect page.
 * - Black Cherry, built into the sheet. The person pastes their own API key here; it's
 *   saved in this browser under the same names Black Cherry reads (cc_ai_provider,
 *   cc_<provider>_key), so the sheet opens with Black Cherry ready. Calls go from the browser
 *   straight to the AI company, on the person's own key.
 */
import React, { useState } from "react";
import { ROUTES } from "../../../../constants";
import { AI_WAYS } from "./libraryLookText";
import LinkIcon from "@mui/icons-material/Link";
import cherry from "../../../../assets/brand/logo_site_onlight.svg";

type Provider = "openai" | "anthropic" | "gemini";

// Same names and "where to find it" as Black Cherry's own key form (grid/draggableToolbar.jsx).
const PROVIDERS: Record<Provider, { label: string; keyName: string; hint: string; url: string; where: string }> = {
  openai: { label: "ChatGPT", keyName: "cc_openai_key", hint: "sk-…", url: "https://platform.openai.com/api-keys", where: "platform.openai.com → API keys" },
  anthropic: { label: "Claude", keyName: "cc_anthropic_key", hint: "sk-ant-…", url: "https://platform.claude.com/settings/keys", where: "platform.claude.com → Get API key" },
  gemini: { label: "Gemini", keyName: "cc_gemini_key", hint: "AIza…", url: "https://aistudio.google.com/apikey", where: "aistudio.google.com → Get API key" },
};

/** Black Cherry's mark: the CherryClipz cherry in white on the purple-to-cherry of its panel. */
export const BlackCherryMark: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <span className="bc-mark" style={{ width: size, height: size }}>
    <img src={cherry} alt="Black Cherry" style={{ height: size * 0.62 }} />
  </span>
);

const read = (key: string): string => {
  try { return localStorage.getItem(key) || ""; } catch { return ""; }
};

const AiWays: React.FC<{ onOpenBlackCherry: () => void }> = ({ onOpenBlackCherry }) => {
  const [provider, setProvider] = useState<Provider>(() => {
    const saved = read("cc_ai_provider");
    return saved === "anthropic" || saved === "gemini" ? saved : "openai";
  });
  const p = PROVIDERS[provider];
  const [saved, setSaved] = useState(() => !!read(p.keyName));
  const [draft, setDraft] = useState("");
  const [bcOpen, setBcOpen] = useState(false);

  const choose = (next: Provider) => {
    setProvider(next);
    setSaved(!!read(PROVIDERS[next].keyName));
    setDraft("");
  };

  const save = () => {
    const key = draft.trim();
    if (!key) return;
    try {
      localStorage.setItem("cc_ai_provider", provider);
      localStorage.setItem(p.keyName, key);
    } catch { return; }
    setSaved(true);
    setDraft("");
  };

  const useThis = () => {
    try { localStorage.setItem("cc_ai_provider", provider); } catch { /* ignore */ }
    onOpenBlackCherry();
  };

  return (
    <div className="aiw">
      <div className="aiw-pick">
        <button type="button" className={`aiw-tile${bcOpen ? " on" : ""}`} aria-expanded={bcOpen} onClick={() => setBcOpen((o) => !o)}>
          <b><BlackCherryMark size={22} /> {AI_WAYS.fast} <em className="fast">{AI_WAYS.fastBadge}</em></b>
          <span>{AI_WAYS.fastNote}</span>
          <i>{saved ? AI_WAYS.ready : AI_WAYS.setUp} {bcOpen ? "▴" : "▾"}</i>
        </button>
        <a className="aiw-tile best" href={ROUTES.AI_CONNECTOR} target="_blank" rel="noopener noreferrer">
          <b><span className="mcp-mark"><img src={cherry} alt="" /><LinkIcon sx={{ fontSize: 15 }} /></span> {AI_WAYS.app} <em>{AI_WAYS.best}</em></b>
          <span>{AI_WAYS.appNote}</span>
          <i>{AI_WAYS.appGo} →</i>
        </a>
      </div>
      {bcOpen && (
        <div className="aiw-way">
          <div className="aiw-tabs">
            {(Object.keys(PROVIDERS) as Provider[]).map((k) => (
              <button type="button" key={k} className={provider === k ? "on" : ""} onClick={() => choose(k)}>{PROVIDERS[k].label}</button>
            ))}
          </div>
          {saved ? (
            <div className="aiw-row">
              <span className="aiw-ok">✓ {AI_WAYS.saved(p.label)}</span>
              <button type="button" className="pill red" onClick={useThis}>{AI_WAYS.openBlackCherry} →</button>
            </div>
          ) : (
            <form className="aiw-row" onSubmit={(e) => { e.preventDefault(); save(); }}>
              <span className="aiw-step">{AI_WAYS.step1} <a href={p.url} target="_blank" rel="noopener noreferrer">{p.where}</a>, {AI_WAYS.step2}</span>
              <input type="password" autoComplete="off" value={draft} placeholder={p.hint} onChange={(e) => setDraft(e.target.value)} />
              <button type="submit" className="pill red" disabled={!draft.trim()}>{AI_WAYS.save}</button>
            </form>
          )}
          <div className="aiw-fine">{AI_WAYS.privacy}</div>
        </div>
      )}
    </div>
  );
};

export default AiWays;
